import React, { Fragment } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';

import { borderRadius } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import { useDiffExpansionState } from 'src/sections/repository/sections/pull-request/hooks/diff-expansion-state';
import { Diff } from 'src/types/pull-request';

import {
  usePlaceholderDiffDetails as usePlaceholderDiffDetailsDI,
  fileHeaderHeight,
} from './hooks/placeholder-diff-details';

const PlaceholderWrapper = styled.pre<{
  estimatedHeight: number;
  isOpen?: boolean;
  'data-testid': string;
}>(({ estimatedHeight, isOpen }) => ({
  height: `${isOpen ? estimatedHeight : fileHeaderHeight}px`,
  marginBottom: token('space.150'),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  font: token('font.body'),
  fontFamily: token('font.family.code'),
  border: `1px solid ${token('color.border')}`,
  borderRadius: `${borderRadius()}px ${borderRadius()}px 0 0`,
  background: `repeating-linear-gradient(${token(
    'color.background.accent.gray.subtlest'
  )}, ${token(
    'color.background.accent.gray.subtlest'
  )} ${fileHeaderHeight}px, ${token(
    'color.background.accent.gray.subtler'
  )} ${fileHeaderHeight}px, ${token('color.background.accent.gray.subtler')} ${
    fileHeaderHeight + 1
  }px, ${token('elevation.surface')} ${fileHeaderHeight}px, ${token(
    'elevation.surface'
  )} 100%)`,
  color: token('color.text.inverse'),
  overflow: 'hidden',
  wordBreak: 'break-all',
}));

const placeholderHeader = {
  font: token('font.body'),
  fontFamily: token('font.family.code'),
  color: token('color.text.inverse'),
};

type DiffPlaceholderProps = {
  file: Diff;
  /* eslint react/no-unused-prop-types:warn */
  diffExpansionState?: boolean;
  usePlaceholderDiffDetails?: typeof usePlaceholderDiffDetailsDI;
};

export const DiffPlaceholder = React.memo(
  ({
    file,
    usePlaceholderDiffDetails = usePlaceholderDiffDetailsDI,
  }: DiffPlaceholderProps) => {
    const filepath = file.to === '/dev/null' ? file.from : file.to;

    const diffExpansionState = useDiffExpansionState({ filepath });

    const wasExpandedByUser = diffExpansionState === true;
    const untouchedByUser = diffExpansionState === undefined;

    const isOpen = wasExpandedByUser || (untouchedByUser && !file.deleted);

    const { estimatedHeight, indexableCommentsAndAuthors } =
      usePlaceholderDiffDetails({
        file,
        isOpen,
      });

    return (
      <PlaceholderWrapper
        estimatedHeight={estimatedHeight}
        isOpen={isOpen}
        data-testid="pr-diff-placeholder-wrapper"
      >
        {isOpen ? (
          <Fragment>
            <h2 style={placeholderHeader}>{filepath}</h2>
            {(file.chunks || []).map(chunk =>
              chunk.changes.map(
                change =>
                  `\n${change.oldLine || ''},${change.newLine || ''} ${
                    change.content
                  }`
              )
            )}
          </Fragment>
        ) : null}
        {indexableCommentsAndAuthors}
      </PlaceholderWrapper>
    );
  }
);
